<template>
  <div class="mx-8 -mt-4 mb-4 relative">
    <div class="pt-4 flex flex-row flex-nowrap items-center space-x-2">
      <yb-icon class="w-12 mr-2 flex-shrink-0" :icon="iconName" :class="iconClasses" />
      <div>
        <div class="font-semibold text-lg">
          {{ messageDisplay }}
        </div>
        <div v-if="errorDetailShown" class="py-2 font-light text-base">
          {{ errorDisplay }}
        </div>
      </div>
    </div>
    <yb-button
      v-if="errorDetailShown"
      v-tooltip="!isResponseError ? '' : 'Copy full error details to clipboard'"
      v-yb-clipboard:function="errorDetail"
      class="font-semibold mt-8"
      :class="isResponseError ? 'absolute -right-10 -top-8' : 'yb-link'"
      :label="isResponseError ? '' : 'Copy full error details to clipboard'"
      icon="clipboard"
      clipboard-label="(Error Details)"
    />
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import errorReporter from './errorReporter'
import { reducedErrorReporting } from '@/services/errorHandler'
import openReplayService from '@/services/openreplay'

const props = defineProps({
  error: {
    type: Error,
    required: true
  },
  message: {
    type: String,
    required: false,
    default: 'An unexpected error occurred.'
  },
  type: {
    type: String,
    required: false,
    default: 'error'
  }
})

const emit = defineEmits(['title', 'nobuttons'])

const messageDisplay = computed(() => {
  let result = props.message || 'An unexpected error occurred.'
  const errorIndex = result.indexOf(String(props.error))
  if (errorIndex > 0) {
    result = result.substring(0, errorIndex) + result.substring(errorIndex + String(props.error).length)
  }
  result = result.trim()
  if (!result.endsWith('.')) {
    result += '.'
  }
  return result
})

const errorDisplay = computed(() => {
  let result = props.error || ''
  const errorRE = /^(TypeError|ApolloError|ResponseError): /
  if (String(result).match(errorRE)) {
    result = String(result).replace(errorRE, '')
  }
  return result
})

const errorDetailShown = computed(() => !reducedErrorReporting())

const isResponseError = computed(() => {
  const { error } = props

  // Is this an application error?
  if (error?.applicationError) {
    return true
  }

  // Is this a SQLState we know about, and can soften the display a bit for?
  const SQLState = error?.response?.sqlState || error?.response?.SQLState || error?.response?.error?.SQLState || error?.response?.errorCode
  return String(SQLState).match(/^(42|5|WL001|WL002|WL003|WL018)/)
})

const iconClasses = computed(() => {
  const result = []
  if (props.type === 'warning' || isResponseError.value) {
    result.push('text-yb-pumpkin')
  } else {
    result.push('text-yb-lava')
  }
  return result
})

const iconName = computed(() => {
  return isResponseError.value ? 'warning' : 'error'
})

const errorDetail = () => errorReporter(props.error, messageDisplay.value)

onMounted(function() {
  const { error, type, message } = props
  if (errorDetailShown.value) {
    console.error(`${type}: ${message}`, error)
  } else {
    console.log(`${type}: ${message}`)
  }
  openReplayService.track('error.response', { message: messageDisplay.value, error: error.message || error.error })
  if (error instanceof Error) {
    openReplayService.error(error, { message: messageDisplay.value })
  }

  if (isResponseError.value) {
    emit('title', 'Notice')
    emit('nobuttons')
  }
})

</script>
